import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarCheck, Search, CheckCircle2,
  Loader2, CircleDot, Ban, RefreshCcw,
  Users, ArrowLeft
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabaseClient";
import { useToast } from "../ui/Toast";

const normalizeEventTime = (value) => {
  const firstTime = String(value || "10:00").split("-")[0].trim().toLowerCase();
  const match = firstTime.match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/);
  if (!match) return "10:00";

  let hour = Number(match[1]);
  const minute = match[2];
  if (match[3] === "pm" && hour < 12) hour += 12;
  if (match[3] === "am" && hour === 12) hour = 0;
  return `${String(hour).padStart(2, "0")}:${minute}`;
};

const AdminAttendance = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [colleges, setColleges] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState("all");
  const [selectedEventId, setSelectedEventId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [dateFilter, setDateFilter] = useState(new Date().toISOString().slice(0, 10));
  const [eventTime, setEventTime] = useState("10:00");
  const [eventHours, setEventHours] = useState("4");

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingAttendance, setSavingAttendance] = useState(false);

  // Load Colleges & Events
  useEffect(() => {
    const initData = async () => {
      const [colRes, evRes] = await Promise.all([
        supabase.from("colleges").select("id, name").order("name"),
        supabase.from("events").select("id, title, date, time, college_name").order("created_at", { ascending: false }),
      ]);
      if (colRes.data) setColleges(colRes.data);
      if (evRes.data) {
        setEvents(evRes.data);
        if (evRes.data.length > 0) setSelectedEventId(evRes.data[0].id);
      }
    };
    initData();
  }, []);

  useEffect(() => {
    const selectedEvent = events.find((event) => event.id === selectedEventId);
    if (selectedEvent?.time) setEventTime(normalizeEventTime(selectedEvent.time));
    if (selectedEvent?.date) setDateFilter(selectedEvent.date);
  }, [selectedEventId, events]);

  // Fetch registered volunteers for selected Event & College
  const loadAttendanceRoster = useCallback(async () => {
    if (!selectedEventId) return;
    setLoading(true);

    const activeEvent = events.find((e) => e.id === selectedEventId);
    const eventName = activeEvent ? activeEvent.title : "";

    const { data: regData, error: regError } = await supabase
      .from("event_registrations")
      .select("volunteer_id, volunteers(id, name, email, college_name)")
      .eq("event_id", selectedEventId);

    if (regError) {
      toast.error("Failed to load registered students: " + regError.message);
      setLoading(false);
      return;
    }

    const { data: attData } = await supabase
      .from("attendance_log")
      .select("*, volunteers(id, name, email, college_name)")
      .eq("event_name", eventName);

    const existingAttMap = new Map((attData || []).map((r) => [r.volunteer_id, r]));

    const combined = (regData || []).map((reg) => {
      const vol = reg.volunteers;
      const existing = existingAttMap.get(reg.volunteer_id);
      if (existing) return existing;

      return {
        id: null,
        volunteer_id: reg.volunteer_id,
        volunteers: vol,
        event_name: eventName,
        attendance_date: dateFilter,
        login_time: `${dateFilter}T${normalizeEventTime(eventTime)}:00`,
        status: "pending",
        marked_at: null,
      };
    });

    setRecords(combined);
    setLoading(false);
  }, [selectedEventId, events, dateFilter, eventTime, toast]);

  useEffect(() => {
    if (selectedEventId && events.length > 0) {
      loadAttendanceRoster();
    }
  }, [selectedEventId, events, loadAttendanceRoster]);

  const toggleAttendance = (target) => {
    setRecords((previous) => previous.map((record) => (
      record.volunteer_id === target.volunteer_id
        ? { ...record, status: record.status === "present" ? "absent" : "present" }
        : record
    )));
  };

  const saveAttendance = async () => {
    const activeEvent = events.find((event) => event.id === selectedEventId);
    if (!activeEvent || filteredRecords.length === 0) {
      toast.error("Select an event with registered students first.");
      return;
    }

    setSavingAttendance(true);
    const markedAt = new Date().toISOString();
    const payloads = filteredRecords.map((record) => ({
      volunteer_id: record.volunteer_id,
      event_name: activeEvent.title,
      attendance_date: dateFilter,
      event_time: normalizeEventTime(eventTime),
      hours: Number(eventHours),
      status: record.status === "present" ? "present" : "absent",
      marked_by: user?.id,
      marked_at: markedAt,
      login_time: record.login_time || `${dateFilter}T${normalizeEventTime(eventTime)}:00`,
    }));

    const results = await Promise.all(filteredRecords.map((record, index) => (
      record.id
        ? supabase.from("attendance_log").update(payloads[index]).eq("id", record.id).select().single()
        : supabase.from("attendance_log").insert(payloads[index]).select().single()
    )));
    const failed = results.find((result) => result.error);

    if (failed) {
      toast.error("Failed to save attendance: " + failed.error.message);
    } else {
      setRecords((previous) => previous.map((record) => {
        const index = filteredRecords.findIndex((item) => item.volunteer_id === record.volunteer_id);
        return index === -1 ? record : { ...record, ...results[index].data };
      }));
      toast.success("Complete attendance saved successfully");
    }
    setSavingAttendance(false);
  };

  const filteredRecords = records.filter((r) => {
    const vol = r.volunteers;
    if (!vol) return false;

    if (selectedCollege !== "all" && vol.college_name !== selectedCollege) return false;
    if (statusFilter !== "all" && r.status !== statusFilter) return false;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return vol.name?.toLowerCase().includes(q) || vol.email?.toLowerCase().includes(q);
    }
    return true;
  });

  const availableEvents = events.filter((e) => {
    if (selectedCollege === "all") return true;
    return !e.college_name || e.college_name === selectedCollege;
  });

  const pendingCount = filteredRecords.filter((r) => r.status === "pending").length;
  const presentCount = filteredRecords.filter((r) => r.status === "present").length;
  const absentCount = filteredRecords.filter((r) => r.status === "absent").length;

  return (
    <div className="min-h-screen bg-ku-cream">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1a1a2e] via-[#2d1b4e] to-[#3B1E60] text-white py-8 md:py-10 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <button
              type="button"
              onClick={() => navigate("/admin/dashboard")}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-ku-gold hover:text-amber-300 transition-colors bg-transparent border-none cursor-pointer mb-3 p-0"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Event Attendance</h1>
            <p className="text-white/60 text-sm mt-1 font-medium">Filter by college and mark event attendance</p>
          </div>
          <button
            type="button"
            onClick={loadAttendanceRoster}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white text-sm font-bold hover:bg-white/20 transition-colors cursor-pointer self-start sm:self-auto"
          >
            <RefreshCcw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Filter Controls */}
        <div className="bg-white rounded-2xl border border-ku-soft/50 shadow-sm p-5 mb-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="block text-[10px] font-black text-ku-purple uppercase tracking-wider mb-1.5">1. Select College</label>
              <select
                value={selectedCollege}
                onChange={(e) => setSelectedCollege(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-ku-purple bg-ku-cream/30 focus:border-ku-purple outline-none"
              >
                <option value="all">All Colleges</option>
                {colleges.map((c) => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-black text-ku-purple uppercase tracking-wider mb-1.5">2. Select Event</label>
              <select
                value={selectedEventId}
                onChange={(e) => setSelectedEventId(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-ku-purple bg-ku-cream/30 focus:border-ku-purple outline-none"
              >
                {availableEvents.length === 0 ? (
                  <option value="">No events found</option>
                ) : (
                  availableEvents.map((e) => (
                    <option key={e.id} value={e.id}>{e.title} ({e.date})</option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-black text-ku-purple uppercase tracking-wider mb-1.5">Attendance Date</label>
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-ku-purple bg-ku-cream/30 outline-none"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black text-ku-purple uppercase tracking-wider mb-1.5">Event Time</label>
              <input
                type="time"
                value={eventTime}
                onChange={(e) => setEventTime(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-ku-purple bg-ku-cream/30 outline-none"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black text-ku-purple uppercase tracking-wider mb-1.5">Hours Credited</label>
              <input
                type="number"
                min="0.5"
                step="0.5"
                value={eventHours}
                onChange={(e) => setEventHours(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-ku-purple bg-ku-cream/30 outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search student by name or email..."
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-200 text-sm font-bold text-ku-purple outline-none"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm font-bold text-ku-purple outline-none"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="present">Present</option>
              <option value="absent">Absent</option>
            </select>
          </div>
        </div>

        {/* Stat Pills */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-ku-soft/50 shadow-sm">
            <Users className="w-4 h-4 text-ku-purple" />
            <span className="text-sm font-bold text-gray-700">{filteredRecords.length} Registered</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-50 border border-amber-200">
            <CircleDot className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-bold text-amber-700">{pendingCount} Pending</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-50 border border-emerald-200">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-bold text-emerald-700">{presentCount} Present</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-50 border border-red-200">
            <Ban className="w-4 h-4 text-red-600" />
            <span className="text-sm font-bold text-red-700">{absentCount} Absent</span>
          </div>
        </div>

        {/* Attendance Table */}
        <div className="bg-white rounded-2xl border border-purple-100 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-ku-purple animate-spin" />
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <CalendarCheck className="w-12 h-12 mb-3 text-purple-300" />
              <p className="font-bold text-gray-500">No students registered for this event yet</p>
            </div>
          ) : (
            <div className="attendance-table-scroll w-full overflow-x-hidden">
              <table className="w-full table-fixed text-left border-collapse">
                <colgroup>
                  <col className="w-[65%]" />
                  <col className="w-[35%]" />
                </colgroup>
                <thead className="bg-[#FAF7FD] border-b border-purple-100">
                  <tr className="text-[11px] uppercase tracking-wider font-extrabold text-purple-900">
                    <th className="py-4 px-5">Volunteer</th>
                    <th className="py-4 px-5 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {filteredRecords.map((record) => {
                  const vol = record.volunteers;
                  const isPresent = record.status === "present";

                    return (
                      <tr key={record.id || record.volunteer_id} className="hover:bg-purple-50/30">
                        <td className="h-[76px] py-4 px-3 sm:px-5 align-middle">
                          <div className="font-bold text-gray-900">{vol?.name || "—"}</div>
                          <div className="text-xs text-gray-400 font-medium break-words">{vol?.email || "—"}</div>
                        </td>
                        <td className="h-[76px] py-4 px-2 sm:px-5 align-middle">
                          <div className="flex items-center justify-center">
                            <label className="inline-flex items-center gap-2 text-sm font-bold text-ku-purple cursor-pointer">
                              <input type="checkbox" checked={isPresent} onChange={() => toggleAttendance(record)} disabled={savingAttendance} className="h-5 w-5 accent-emerald-600 cursor-pointer" />
                              <span className="whitespace-nowrap">{isPresent ? "Present" : "Absent"}</span>
                            </label>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
          {filteredRecords.length > 0 && (
            <div className="flex justify-end mt-5">
              <button type="button" onClick={saveAttendance} disabled={savingAttendance} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-ku-purple text-white text-sm font-bold hover:bg-ku-violet transition-colors border-none cursor-pointer disabled:opacity-60">
                {savingAttendance ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {savingAttendance ? "Saving Attendance..." : "Update Attendance"}
              </button>
            </div>
          )}
      </div>
    </div>
  );
};

export default AdminAttendance;